import { Fragment, useEffect, useRef, useState, useTransition } from "react"
import { useTranslation } from "react-i18next"
import { fetchQuery } from "react-relay"
import { Command as CommandPrimitive } from "cmdk"
import { HighlightedText } from "@/components/highlighted-text"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { CommandInput, CommandItem, CommandSeparator } from "@/components/ui/command"
import { FormError } from "@/components/ui/form"
import { useClickOutside } from "@/hooks/useClickOutside"
import { cn } from "@/lib/utils"
import { FreeTextMedicationCreation } from "@/modules/patients/detail/medication/components/free-text-medication-creation"
import { DeMedicationDrawer } from "@/modules/patients/detail/medication/de/de-medication-drawer"
import {
  MedicationSearchResultsType,
  PrescriptionMedicationSearchResults,
} from "@/modules/patients/detail/medication/de/search-results"
import { isFreeTextMedicationType } from "@/modules/patients/detail/medication/lib/is-free-text-medication-type"
import { useDeMedicationContext } from "@/modules/patients/detail/medication/store/useDeMedicationStore"
import {
  MAX_MEDICATION_SEARCH_CHARACTERS,
  MIN_MEDICATION_SEARCH_CHARACTERS,
} from "@/modules/patients/detail/medication/types/medication-config"
import { MEDICATION_MODES } from "@/modules/patients/detail/medication/types/medication-mode"
import { MedicationSearchResults } from "@/modules/patients/detail/prescription/de/medication-search-results"
import { PrescriptionDataType } from "@/modules/patients/detail/prescription/de/schema/prescription-schema"
import { useDePrescriptionStoreContext } from "@/modules/patients/detail/prescription/de/store/usePrescriptionStore"
import { useDeMedicationSearchQuery } from "@/modules/patients/services/de/__generated__/useDeMedicationSearchQuery.graphql"
import { deMedicationSearchQuery } from "@/modules/patients/services/de/useDeMedicationSearchQuery"
import { RelayEnvironment } from "@/relay-environment"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { DeMedicationType } from "@/types/__generated__/union-types"

type ExistingMedicationType = Record<"value" | "label", string> & {
  category?: string | null
  type: DeMedicationType | null
}

type MedicationGroup = {
  title: string
  medications: ExistingMedicationType[]
}

type MedicationSearchProps = {
  existingMedicationsGroups: MedicationGroup[]
  error?: string
  disabled?: boolean
  prescriptionData: PrescriptionDataType
  onSelect?: (value: ExistingMedicationType) => void
}

const MedicationSearch = ({
  existingMedicationsGroups,
  error,
  disabled,
  prescriptionData,
  onSelect,
}: MedicationSearchProps) => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const resultRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isDisplaySearchText, setIsDisplaySearchText] = useState(false)
  const [isDisplayNotExistingMedication, setIsDisplayNotExistingMedication] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [searchResults, setSearchResults] = useState<useDeMedicationSearchQuery["response"] | null>(null)
  const [isLoading, startTransition] = useTransition()

  const setUnSavedMedicationIds = useDePrescriptionStoreContext((state) => state.setUnSavedMedicationIds)
  const openDrawerWithoutConfirmation = useDrawerStore((state) => state.openDrawerWithoutConfirmation)
  const setDeMedicationDrawerMode = useDeMedicationContext((state) => state.setDeMedicationDrawerMode)

  const { de_preparations, de_substances } = { ...searchResults }
  const dePreparationsResult = de_preparations?.result
  const deSubstancesResult = de_substances?.result
  const hasSearchResults =
    (dePreparationsResult && dePreparationsResult.length > 0) || (deSubstancesResult && deSubstancesResult.length > 0)

  const hasExistingMedications = existingMedicationsGroups.some(
    (group) => group.medications && group.medications.length > 0,
  )
  const isOpenOnFocus = inputValue.trim().length >= MIN_MEDICATION_SEARCH_CHARACTERS || hasExistingMedications

  useClickOutside((event: MouseEvent) => {
    if (isOpen && resultRef.current && !resultRef.current.contains(event.target as HTMLElement)) {
      setIsOpen(false)
    }
  }, inputRef)

  useEffect(() => {
    if (!isLoading) {
      if (hasSearchResults) setIsDisplaySearchText(false) // Display search results
      if (!hasSearchResults) setIsDisplayNotExistingMedication(true) // Display "Add" unknown medication text if search results is empty
    }
  }, [isLoading, hasSearchResults])

  const handleInputChange = (value: string) => {
    setInputValue(value)
    if (inputRef.current) inputRef.current.value = value

    if (value.trim().length >= MIN_MEDICATION_SEARCH_CHARACTERS) {
      setIsOpen(true)
      setIsDisplaySearchText(true)
      setIsDisplayNotExistingMedication(false)
    } else {
      setIsDisplaySearchText(false)
      setSearchResults(null)
      if (!hasExistingMedications) setIsOpen(false)
    }
  }

  const handleSearchSubmit = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const searchTerm = inputValue.trim()
    if (event.key === "Enter" && searchTerm.length >= MIN_MEDICATION_SEARCH_CHARACTERS) {
      startTransition(() => {
        return new Promise((resolve) => {
          fetchQuery<useDeMedicationSearchQuery>(RelayEnvironment, deMedicationSearchQuery, { searchTerm }).subscribe({
            next: (data) => {
              setSearchResults(data)
              resolve()
            },
          })
        })
      })
    }
  }

  const handleSelectExistingMedication = (selectedMedication: ExistingMedicationType) => {
    onSelect?.(selectedMedication)
    setUnSavedMedicationIds(selectedMedication.value)
    setIsOpen(false)
    setInputValue("")
  }

  const handleSelectSearchItem = (selectedMedication: MedicationSearchResultsType) => {
    const prescriptionMedicationSearchResults: PrescriptionMedicationSearchResults = {
      ...selectedMedication,
      ...prescriptionData,
    }
    openDrawerWithoutConfirmation(
      () => <DeMedicationDrawer prescriptionMedicationSearchResults={prescriptionMedicationSearchResults} />,
      "MedicationDrawer",
      {
        onBeforeOpen: () => setDeMedicationDrawerMode(MEDICATION_MODES.CREATION),
      },
    )
  }

  const onFreeTextMedicationCreation = () => {
    const prescriptionMedication: PrescriptionMedicationSearchResults = {
      freeTextMedicationName: inputValue,
      ...prescriptionData,
    }
    openDrawerWithoutConfirmation(
      () => <DeMedicationDrawer prescriptionMedicationSearchResults={prescriptionMedication} />,
      "MedicationDrawer",
      {
        onBeforeOpen: () => setDeMedicationDrawerMode(MEDICATION_MODES.CREATION),
      },
    )
  }

  const renderSearchText = () => {
    return isDisplayNotExistingMedication ? (
      <FreeTextMedicationCreation searchTerm={inputValue} onClick={onFreeTextMedicationCreation} />
    ) : (
      <p className="text-on-surface-subdued px-4 py-2 text-sm">{t("common.pressEnterToSearch")}</p>
    )
  }

  return (
    <CommandPrimitive className="relative" shouldFilter={false}>
      <CommandInput
        ref={inputRef}
        value={inputValue}
        onValueChange={handleInputChange}
        onFocus={() => isOpenOnFocus && setIsOpen(true)}
        onKeyDown={handleSearchSubmit}
        placeholder={t("patient.prescription.searchProductOrSubstance")}
        disabled={disabled}
        className={cn("bg-surface pr-12", error && "outline-critical! outline-2")}
        isLoading={isLoading}
        maxLength={MAX_MEDICATION_SEARCH_CHARACTERS}
        data-testid="search-input-medication"
      />
      {error && <FormError>{error}</FormError>}
      {isOpen && !isLoading && (
        <div
          ref={resultRef}
          className="bg-surface shadow-4 animate-in fade-in-0 zoom-in-95 absolute top-12.75 z-10 w-full rounded-xl outline-hidden">
          <ScrollWrapper className="max-h-80 py-2" testId="medication-search-results">
            {isDisplaySearchText ? (
              renderSearchText()
            ) : (
              <>
                {hasExistingMedications && (
                  <>
                    {existingMedicationsGroups.map(({ title, medications }, index) => {
                      if (medications.length > 0) {
                        return (
                          <Fragment key={title}>
                            {index !== 0 && existingMedicationsGroups[index - 1].medications.length > 0 && (
                              <CommandSeparator className="mb-0" alwaysRender />
                            )}
                            <div
                              role="group"
                              data-testid={`${title.toLowerCase()}-medication-section`}
                              className="text-foreground overflow-hidden px-2">
                              <div className="text-on-surface-subdued px-2 py-2.5 text-sm font-medium">{title}</div>
                              {medications.map((medication) => (
                                <CommandItem
                                  key={medication.value}
                                  className="cursor-pointer rounded-md"
                                  value={medication.value}
                                  onSelect={() => handleSelectExistingMedication(medication)}
                                  data-testid="medication-search-item">
                                  {hasSearchResults ? (
                                    <div className="grid">
                                      <HighlightedText
                                        className="text-on-surface truncate text-sm"
                                        title={medication.label}
                                        text={medication.label}
                                        searchValue={inputValue.trim()}
                                        extendedMarkClasses="font-normal"
                                      />
                                    </div>
                                  ) : (
                                    <div className="grid">
                                      <p
                                        className={cn(
                                          "truncate",
                                          isFreeTextMedicationType(medication.type) && "pr-1 italic",
                                        )}>
                                        {medication.label}
                                      </p>
                                    </div>
                                  )}
                                </CommandItem>
                              ))}
                            </div>
                          </Fragment>
                        )
                      }
                    })}
                    {hasSearchResults && <CommandSeparator className="mb-0" alwaysRender />}
                  </>
                )}

                {hasSearchResults && (
                  <MedicationSearchResults
                    searchValue={inputValue.trim()}
                    preparations={dePreparationsResult ?? []}
                    substances={deSubstancesResult ?? []}
                    handleSelectSearchItem={handleSelectSearchItem}
                  />
                )}
              </>
            )}
          </ScrollWrapper>
        </div>
      )}
    </CommandPrimitive>
  )
}

export { MedicationSearch }
